import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, cachedGet } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";

const EMIRATES = ["All", "Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain"];

export default function Areas() {
  const [areas, setAreas] = useState([]);
  const [emirate, setEmirate] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Stale-while-revalidate: paint cached list instantly on repeat visits,
      // then silently refresh in the background so admin edits still show up.
      try {
        const first = await cachedGet("/communities", { params: { limit: 200 } });
        if (cancelled) return;
        setAreas(first.data.items || []);
        if (first.isStale) {
          const fresh = await first.refresh;
          if (!cancelled) setAreas(fresh.items || []);
        }
      } catch {
        try { const r = await api.get("/areas"); if (!cancelled) setAreas(r.data.items || []); } catch {}
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Group by emirate for the tab counts
  const emirateCounts = useMemo(() => {
    const c = { All: areas.length };
    areas.forEach((a) => { c[a.emirate || "Dubai"] = (c[a.emirate || "Dubai"] || 0) + 1; });
    return c;
  }, [areas]);

  const filtered = useMemo(() => {
    let list = areas;
    if (emirate !== "All") list = list.filter((a) => (a.emirate || "Dubai") === emirate);
    if (q) {
      const qq = q.toLowerCase();
      list = list.filter((a) =>
        (a.name || "").toLowerCase().includes(qq) ||
        (a.tagline || "").toLowerCase().includes(qq) ||
        (a.description || "").toLowerCase().includes(qq)
      );
    }
    return list;
  }, [areas, emirate, q]);

  return (
    <div data-testid="areas-page">
      <SEO
        title="UAE community guides"
        description="Curated UAE community guides — where to buy, live and let. Market data, lifestyle, connectivity and investment analysis for every neighbourhood we cover."
        path="/areas"
      />
      <section className="pt-6 pb-10 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Communities", path: "/areas" }]} />
          <div className="overline text-[#001588] mb-4">Community guides · {areas.length}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-tight max-w-4xl">Where to buy, live and let in the UAE.</h1>

          {/* Emirate filter tabs */}
          <div className="mt-8 flex flex-wrap gap-2" data-testid="emirate-tabs">
            {EMIRATES.map((e) => (
              <button
                key={e}
                onClick={() => setEmirate(e)}
                data-testid={`emirate-tab-${e.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                  emirate === e ? "bg-[#001588] text-white border-[#001588]" : "bg-white text-neutral-700 border-neutral-200 hover:border-[#001588] hover:text-[#001588]"
                }`}
              >
                {e}
                {emirateCounts[e] !== undefined && <span className="ml-2 opacity-70">({emirateCounts[e]})</span>}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="mt-6 flex items-center bg-white border border-neutral-300 max-w-lg">
            <MagnifyingGlass size={16} className="mx-3 text-neutral-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search communities…"
              className="flex-1 py-2.5 bg-transparent text-sm text-[#050d19] placeholder:text-neutral-400 focus:outline-none pr-4"
              data-testid="areas-search"
            />
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          {filtered.length === 0 ? (
            <div className="text-center py-14 text-neutral-500 text-sm">No communities match your filters.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filtered.map((a) => (
                <Link key={a.slug} to={`/areas/${a.slug}`} data-testid={`area-card-${a.slug}`} className="group block">
                  <div className="pp-hover-zoom aspect-[4/3] mb-5 bg-neutral-100">
                    {a.hero_image && (
                      <img src={a.hero_image} alt={a.name} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    )}
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="overline text-[#001588] mb-2 flex items-center gap-2">
                        <span className="uppercase">{a.emirate || "Dubai"}</span>
                        {a.average_price_apartment && <span className="opacity-60">· AED {a.average_price_apartment}/sqft</span>}
                      </div>
                      <h2 className="font-serif text-2xl group-hover:text-[#001588] transition-colors">{a.name}</h2>
                      <p className="text-neutral-500 mt-2 text-sm line-clamp-3">{a.tagline || a.description}</p>
                      {(a.rental_yield || a.investment_score) && (
                        <div className="flex gap-3 mt-3 text-xs text-neutral-500">
                          {a.rental_yield && <span>Yield <b className="text-[#001588]">{a.rental_yield}%</b></span>}
                          {a.investment_score && <span>· Score <b className="text-[#001588]">{a.investment_score}/10</b></span>}
                        </div>
                      )}
                    </div>
                    <ArrowRight size={20} className="text-neutral-400 group-hover:text-[#001588] mt-1 transition-colors flex-shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
